<script setup lang="ts">
    import { Badge } from '@/components/ui/badge';
    import { CardTitle } from '@/components/ui/card';
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import PageCard from '@/components/PageCard.vue';
    import type { Payroll, HolidayPayObligation } from '@/types';

    const { t } = useI18n();

    const props = withDefaults(
        defineProps<{
            payroll: Payroll | HolidayPayObligation;
            isApproved?: boolean;
            showBadge?: boolean;
        }>(),
        {
            showBadge: true,
        },
    );

    const summaries = computed(() => {
        return [
            {
                label: t('payrolls.summary.month'),
                value: t(`payrolls.edit.months.${props.payroll.month}`),
            },
            {
                label: t('payrolls.summary.year'),
                value: props.payroll.year,
            },
            {
                label: t('payrolls.summary.num_of_employees'),
                value: `${props.payroll.num_of_employees} ${t('payrolls.summary.employees')}`,
            },
            {
                label: t('payrolls.summary.updated_at'),
                value: props.payroll.updated_at_formatted,
            },
        ];
    });
</script>

<template>
    <PageCard class="min-w-sm flex-none self-start lg:flex-0">
        <CardTitle class="mb-2 flex items-center justify-between gap-2">
            {{ t('payrolls.table.headers.status') }}
            <Badge
                v-if="showBadge && isApproved"
                variant="success"
            >
                {{ t('payrolls.edit.approved_text') }}
            </Badge>
            <Badge
                v-else-if="showBadge"
                variant="error"
            >
                {{ t('payrolls.edit.not_approved_text') }}
            </Badge>
        </CardTitle>
        <div
            v-for="summary in summaries"
            :key="summary.label"
            class="flex items-center justify-between gap-2 border-b py-2 text-xs"
        >
            <span class="font-bold">{{ summary.label }}</span>
            <span>{{ summary.value }}</span>
        </div>
        <p
            v-if="isApproved"
            class="text-muted-foreground mt-2 text-xs"
        >
            {{ t('payrolls.edit.awaiting-text-table') }}
        </p>
        <p
            v-else
            class="text-muted-foreground mt-2 text-xs"
        >
            {{ t('payrolls.edit.help-text-table') }}
            <span class="font-bold">{{ t('payrolls.edit.help-text-bold') }}</span>
        </p>
    </PageCard>
</template>
